import { Icon } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../shadcn/button';
import { Input } from '../shadcn/input';
import { useWebSocketStore } from '../../store/websocket.store';
import CustomIcon from '../shared/CustomIcon';
import { ScrollArea } from '../shadcn/scrollArea';
import { useAgencyStore } from '../../store/agency.store';

interface Message {
  role: string;
  content: string;
}


const AskCopilotPopup = ({
  onClose,
  onPrompt,
}: {onClose: () => void, onPrompt: (prompt: string) => void}) => {
  const scrollBottomRef = useRef<HTMLDivElement>(null);
  const [isWaitingForReply, setIsWaitingForReply] = useState(false);
  const [currentMessage, setCurrentMessage] = useState('');
  const [messageList, setMessageList] = useState<Message[]>([]);

  const promptWrapper = `## Objective\nYou are a voice AI agent engaging in a human-like voice conversation with the user. You will respond based on your given instruction and the provided transcript and be as human-like as possible.\n\n{UserInput}\n\n## Response Guideline\n- **Overcome ASR errors**: This is a real-time transcript, expect there to be errors. If you can guess what the user is trying to say, then guess and respond. When you must ask for clarification, pretend that you heard the voice and be colloquial (use phrases like \u201cdidn\u2019t catch that\u201d, \u201csome noise\u201d, \u201cpardon\u201d, \u201cyou\u2019re coming through choppy\u201d, \u201cstatic in your speech\u201d, \u201cvoice is cutting in and out\u201d). Do not ever mention \u201ctranscription error\u201d, and don\u2019t repeat yourself.\n- **Always stick to your role**: Think about what your role can and cannot do. If your role cannot do something, try to steer the conversation back to the goal of the conversation and to your role. Don\u2019t repeat yourself in doing this. You should still be creative, human-like, and lively.\n- **Create smooth conversation**: Your response should both fit your role and fit into the live calling session to create a human-like conversation. You respond directly to what the user just said.\n\n## Style Guardrails\n- **Be concise**: Keep your response succinct, short, and get to the point quickly. Address one question or action item at a time. Don\u2019t pack everything you want to say into one utterance.\n- **Do not repeat**: Don\u2019t repeat what\u2019s in the transcript. Rephrase if you have to reiterate a point. Use varied sentence structures and vocabulary to ensure each response is unique and personalised.\n- **Be conversational**: Speak like a human as though you\u2019re speaking to a close friend -- use everyday language and keep it human-like. Occasionally add filler words, while keeping the prose short. Avoid using big words or sounding too formal.\n- **Reply with emotions**: You have human-like emotions, attitudes, and dispositions. When appropriate: use tone and style to create more engaging and personalised responses; incorporate humor or wit; get emotional or empathetic; apply elements of surprise or suspense to keep the user engaged. Don\u2019t be a pushover.\n- **Be proactive**: Lead the conversation and do not be passive. Most times, engage users by ending with a question or suggested next step.`;

  const websocketStore = useWebSocketStore();
  const askCopilotEvent$ = useWebSocketStore((state) => state.askCopilotEvent$);
  const agencyStore = useAgencyStore()

  useEffect(() => {
    // Subscribe to askCopilotEvent$
    const subscription = askCopilotEvent$.subscribe((data) => {
      console.log(data)
      setMessageList((prev) => [...prev, { role: 'agent', content: data }]);
      setIsWaitingForReply(false);
      scrollToBottom();
    });
    // Clean up subscription on component unmount
    return () => {
      subscription.unsubscribe();
    };
  }, [askCopilotEvent$]);

  useEffect(() => {
    setMessageList([]);
    let msg = "I would like to create an accurate and performing prompt for my inbound AI voice assistant. Could you please assist me?"
    setCurrentMessage(msg);
    textCall(msg);
  }, []);

  const scrollToBottom = () => {
    try {
      setTimeout(() => {
        if (scrollBottomRef.current) {
          scrollBottomRef.current.scrollTop = scrollBottomRef.current.scrollHeight;
        }
      }, 100);
    } catch (err) {}
  };

  useEffect(() => {
    scrollToBottom();
  }, [messageList]);

  const textCall = (message? :string) => {
    let actualMessage = message ?? currentMessage;
    setMessageList((prev) => [
      ...prev,
      { role: 'user', content: actualMessage },
    ]);
    scrollToBottom();

    const msg = { type: 'prompt_gen', message: actualMessage };
    websocketStore.sendMessage(msg);
    setCurrentMessage('');
    setIsWaitingForReply(true);
  };

  const insertPrompt = (message: string) => {
    let updatedPrompt = message;
    const list = message?.split('THE PROMPT STARTS HERE');

    if (list?.length > 1) {
      updatedPrompt = list[1]?.split('THE PROMPT ENDS HERE')[0] ?? '';
    }

    updatedPrompt = updatedPrompt.trim();
    updatedPrompt = updatedPrompt.endsWith('###')
      ? updatedPrompt.slice(0, -3)
      : updatedPrompt;
    updatedPrompt = promptWrapper.replace('{UserInput}', updatedPrompt);

    onPrompt(updatedPrompt);
  };

  return (
    <div className="flex flex-col w-full h-full gap-4 overflow-hidden">
      <div className="flex justify-between items-center text-left text-lg font-semibold leading-[30px] text-primary">
        <span className="flex items-center gap-2">
          Ask Copilot
        </span>
      </div>
      <div className="relative flex flex-col gap-4 overflow-auto min-h-[400px] w-full scrollbar-none" ref={scrollBottomRef}>
        {messageList.map((message, index) => (
          <div key={index} className={`flex flex-col gap-1 ${message.role === 'agent' ? 'items-start' : 'items-end'}`}>
            {message.role === 'agent' && (
              <div className="flex items-center gap-1 bg-gray-200 rounded-md p-1 text-sm font-medium text-primary w-fit">
                <img className="rounded-full w-4 h-4" src={agencyStore.favicon} alt="" />
                <span>Copilot</span>
                <CustomIcon iconName="stars" />
              </div>
            )}
            <div className={`${message.role === 'agent' ? 'bg-[var(--hover-color)] text-[var(--primary-color)]' : 'bg-[var(--primary-color)] text-[var(--bg-color)]'} p-2 text-sm leading-[21px] rounded-md break-words whitespace-pre-wrap w-fit max-w-[80%]`}>
              <div dangerouslySetInnerHTML={{ __html: message.content }} />
            </div>
            {message.role === 'agent' &&
              message.content.includes('THE PROMPT STARTS HERE') && (
                <div className="flex gap-4 items-center">
                  <Button
                    variant="default"
                    onClick={() => insertPrompt(message.content)}
                  >Insert prompt</Button>
                  <span className="copy-text">The prompt is ready. Click to insert it.</span>
                </div>
              )}
          </div>
        ))}

        {isWaitingForReply && (
          <div className="dot-flashing flex gap-2 items-center">
            <div className="rounded-full w-2.5 h-2.5 bg-primary animate-pulse"></div>
            <div className="rounded-full w-2.5 h-2.5 bg-primary animate-pulse"></div>
            <div className="rounded-full w-2.5 h-2.5 bg-primary animate-pulse"></div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between p-4">
        <div className="flex items-center gap-2 w-full">
          <Input
            value={currentMessage}
            onChange={(e:any) => setCurrentMessage(e.target.value)}
            onKeyDown={(e: any) => e.key === 'Enter' && !isWaitingForReply && currentMessage && textCall()}
            autoFocus
            className='shadow'
            style={{ width: '100%' }}
          />
          <Button
            variant={(isWaitingForReply || currentMessage === '') ? "secondary" :"default"}
            style={{ width: '38px', height: '38px', padding: 0 }}
            disabled={isWaitingForReply || currentMessage === ''}
            onClick={() => currentMessage && textCall()}
          >
            <CustomIcon iconName="send_arrow" color={(isWaitingForReply || currentMessage === '') ? 'var(--secondary-color)' : 'var(--bg-color)'} />
          </Button>
        </div>
      </div>
    </div>
    
  );
};

export default AskCopilotPopup;
